import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import FaceDetection from './FaceDetection';

const StudentRoomView = ({ userId, roomId }) => {
  const { socket, isConnected, sendFaceAlert } = useSocket();
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [error, setError] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef(new Map());
  const peerConnections = useRef(new Map());

  // Initialize camera
  const initializeCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: true
      });

      setLocalStream(mediaStream);
      setIsStreamActive(true);
      setConnectionStatus('connected');
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = mediaStream;
      }

      console.log('Camera initialized successfully');
    } catch (err) {
      console.error('Camera initialization failed:', err);
      setError('Gagal mengakses kamera: ' + err.message);
      setConnectionStatus('error');
    }
  }, []);

  // WebRTC peer connection setup
  const createPeerConnection = useCallback((targetSocketId, isInitiator = false) => {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ],
      iceCandidatePoolSize: 10
    };

    const peerConnection = new RTCPeerConnection(configuration);
    
    // Add local stream tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
      });
    }

    // Handle remote streams
    peerConnection.ontrack = (event) => {
      console.log('Received remote track from:', targetSocketId);
      const remoteStream = event.streams[0];
      
      setRemoteStreams(prev => {
        const newMap = new Map(prev);
        newMap.set(targetSocketId, remoteStream);
        return newMap;
      });

      // Update video element if exists
      const videoElement = remoteVideoRefs.current.get(targetSocketId);
      if (videoElement) {
        videoElement.srcObject = remoteStream;
      }
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('ice-candidate', {
          targetSocketId,
          candidate: event.candidate
        });
      }
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      console.log(`Peer connection state with ${targetSocketId}:`, peerConnection.connectionState);
      
      if (peerConnection.connectionState === 'connected') {
        console.log(`Successfully connected to ${targetSocketId}`);
      } else if (peerConnection.connectionState === 'disconnected' || 
                 peerConnection.connectionState === 'failed') {
        // Remove stream if connection fails
        setRemoteStreams(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetSocketId);
          return newMap;
        });
      }
    };

    peerConnection.onicecandidateerror = (event) => {
      console.error('ICE candidate error:', event);
    };

    peerConnections.current.set(targetSocketId, peerConnection);

    // If initiator, create and send offer
    if (isInitiator) {
      peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      })
        .then(offer => peerConnection.setLocalDescription(offer))
        .then(() => {
          console.log('Sending offer to:', targetSocketId);
          socket.emit('offer', {
            targetSocketId,
            offer: peerConnection.localDescription
          });
        })
        .catch(err => console.error('Error creating offer:', err));
    }

    return peerConnection;
  }, [localStream, socket]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Handle offers from supervisors
    socket.on('offer', async ({ offer, senderSocketId }) => {
      console.log('Received offer from:', senderSocketId);
      
      const peerConnection = createPeerConnection(senderSocketId, false);
      
      try {
        await peerConnection.setRemoteDescription(offer);
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        
        socket.emit('answer', {
          targetSocketId: senderSocketId,
          answer: peerConnection.localDescription
        });
      } catch (err) {
        console.error('Error handling offer:', err);
      }
    });

    // Handle ICE candidates
    socket.on('ice-candidate', async ({ candidate, senderSocketId }) => {
      const peerConnection = peerConnections.current.get(senderSocketId);
      if (peerConnection && candidate) {
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('Successfully added ICE candidate from:', senderSocketId);
        } catch (err) {
          console.error('Error adding ICE candidate:', err);
          // Retry after delay
          setTimeout(async () => {
            try {
              await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (retryErr) {
              console.error('Retry failed:', retryErr);
            }
          }, 500);
        }
      }
    });

    // Handle audio control from supervisor
    socket.on('audio-control', ({ muted }) => {
      setIsMuted(muted);
      if (localStream) {
        const audioTrack = localStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !muted;
        }
      }
    });

    // Handle new supervisors joining
    socket.on('user-joined', ({ role, socketId }) => {
      if (role === 'supervisor') {
        console.log('New supervisor joined:', socketId);
        createPeerConnection(socketId, true);
      }
    });

    // Handle room state updates to connect to existing supervisors
    socket.on('room-state', (state) => {
      state.supervisors.forEach(supervisor => {
        if (!peerConnections.current.has(supervisor.socketId)) {
          console.log('Connecting to existing supervisor:', supervisor.socketId);
          createPeerConnection(supervisor.socketId, true);
        }
      });
    });

    // Cleanup peer connections when users leave
    socket.on('user-left', ({ socketId }) => {
      const peerConnection = peerConnections.current.get(socketId);
      if (peerConnection) {
        peerConnection.close();
        peerConnections.current.delete(socketId);
      }
      setRemoteStreams(prev => {
        const newMap = new Map(prev);
        newMap.delete(socketId);
        return newMap;
      });
    });

    return () => {
      socket.off('offer');
      socket.off('ice-candidate');
      socket.off('audio-control');
      socket.off('user-joined');
      socket.off('user-left');
      socket.off('room-state');
    };
  }, [socket, createPeerConnection, localStream]);

  // Initialize camera when component mounts
  useEffect(() => {
    initializeCamera();

    return () => {
      // Cleanup
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      peerConnections.current.forEach(pc => pc.close());
      peerConnections.current.clear();
    };
  }, [initializeCamera]);

  // Face detection alert handler
  const handleFaceAlert = useCallback((direction, confidence) => {
    const messages = {
      'left': `Peserta menoleh ke kiri (confidence: ${Math.round(confidence * 100)}%)`,
      'right': `Peserta menoleh ke kanan (confidence: ${Math.round(confidence * 100)}%)`,
      'up': `Peserta melihat ke atas (confidence: ${Math.round(confidence * 100)}%)`,
      'down': `Peserta melihat ke bawah (confidence: ${Math.round(confidence * 100)}%)`
    };

    sendFaceAlert(direction, messages[direction] || 'Gerakan mencurigakan terdeteksi');
  }, [sendFaceAlert]);

  // Get video ref for remote stream
  const getRemoteVideoRef = (socketId) => {
    if (!remoteVideoRefs.current.has(socketId)) {
      remoteVideoRefs.current.set(socketId, React.createRef());
    }
    return remoteVideoRefs.current.get(socketId);
  };

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full border-l-4 border-red-500">
          <div className="flex items-center mb-4">
            <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="ml-3 text-lg font-semibold text-red-800">Kesalahan Kamera</h2>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={initializeCamera}
            className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-900 relative">
      {/* Local video (hidden by default) */}
      <video
        ref={localVideoRef}
        autoPlay
        muted
        playsInline
        className="hidden"
      />

      {/* Remote videos from supervisors */}
      {Array.from(remoteStreams.entries()).map(([socketId, stream]) => (
        <div key={socketId} className="absolute bottom-4 right-4 w-64 h-48 bg-black rounded-lg overflow-hidden shadow-xl">
          <video
            ref={getRemoteVideoRef(socketId)}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
            onLoadedMetadata={(e) => {
              e.target.srcObject = stream;
            }}
          />
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            Pengawas
          </div>
        </div>
      ))}

      {/* Face Detection Component */}
      {localStream && (
        <FaceDetection
          stream={localStream}
          onFaceAlert={handleFaceAlert}
          isActive={isStreamActive}
        />
      )}

      {/* Status Panel */}
      <div className="absolute top-4 left-4 right-4 z-10">
        <div className="bg-gray-800 bg-opacity-90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Connection Status */}
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' : 
                  connectionStatus === 'connecting' ? 'bg-yellow-500' : 'bg-red-500'
                }`}>
                  {connectionStatus === 'connecting' && (
                    <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse"></div>
                  )}
                </div>
                <span className="text-sm text-gray-300">
                  {connectionStatus === 'connected' ? 'Terhubung' : 
                   connectionStatus === 'connecting' ? 'Menghubungkan...' : 'Terputus'}
                </span>
              </div>

              {/* Socket Status */}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-blue-500' : 'bg-gray-500'}`}></div>
                <span className="text-sm text-gray-300">
                  {isConnected ? 'Server' : 'Offline'}
                </span>
              </div>
            </div>

            {/* Audio Status */}
            <div className="flex items-center space-x-2">
              {isMuted ? (
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
              <span className="text-sm text-gray-300">
                {isMuted ? 'Dibisukan' : 'Audio Aktif'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-white max-w-md mx-auto p-8">
          <div className="mb-8">
            <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">Kamera Aktif</h2>
            <p className="text-gray-300 mb-4">
              Anda sedang dalam pengawasan ujian. Kamera dan mikrofon aktif mengirim data ke pengawas.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6 text-left">
            <h3 className="font-semibold mb-3 text-center">Petunjuk Ujian:</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">•</span>
                <span>Tetap fokus ke layar dan jangan menoleh ke samping</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">•</span>
                <span>Pastikan wajah terlihat jelas di kamera</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">•</span>
                <span>Jangan meninggalkan tempat duduk</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">•</span>
                <span>Pengawas dapat mengatur audio Anda</span>
              </li>
            </ul>
          </div>

          {/* Face Detection Status */}
          <div className="mt-6 p-4 bg-blue-900 bg-opacity-30 rounded-lg">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-blue-300">
                Deteksi Wajah Aktif
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRoomView;